// ============================================
// InvoiceListItem.jsx
// File: src/components/invoices/InvoiceListItem.jsx
// 
// Simplified invoice list item - click to open drawer
// ============================================

import React from 'react';
import { Mail, Check, Users, AlertCircle } from 'lucide-react';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';

const InvoiceListItem = ({ invoice, onClick }) => {
  const customerName = invoice.customerName || invoice.job?.customer?.name || 'Unknown';
  const customerEmail = invoice.customerEmail || invoice.job?.customer?.email;

  const formatEmailSentTime = (date) => {
    if (!date) return '';
    const sentDate = new Date(date);
    const now = new Date();
    const diffMs = now - sentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Invoice Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Invoice Number */}
              <h3 className="font-semibold text-gray-900">
                {invoice.invoiceNumber || `INV-${invoice.id}`}
              </h3>
              
              {/* Email Sent Badge */}
              {invoice.emailSent && (
                <span 
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full"
                  title={`Sent ${formatDate(invoice.emailSentAt)}`}
                >
                  <Check size={10} />
                  Emailed
                </span>
              )}
              
              {/* Consolidated Badge */}
              {invoice.isConsolidated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  <Users size={10} />
                  {invoice.jobCount} jobs
                </span>
              )}
              
              {/* No email indicator */}
              {!customerEmail && (
                <span 
                  className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full"
                  title="No email on file"
                >
                  <AlertCircle size={10} className="mr-1" />
                  No email
                </span>
              )}
            </div>
            
            {/* Customer Name */}
            <p className="text-gray-600 text-sm mt-1 truncate">
              {customerName}
            </p>
            
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
              <span>Issued: {formatDate(invoice.invoiceDate)}</span>
              
              {invoice.dueDate && (
                <span className={invoice.status === 'Overdue' ? 'text-red-600 font-medium' : ''}>
                  Due: {formatDate(invoice.dueDate)}
                </span>
              )}
              
              {invoice.emailSent && (
                <span className="text-green-600">
                  Sent: {formatEmailSentTime(invoice.emailSentAt)}
                </span>
              )}
            </div>
          </div>

          {/* Right: Amount & Status */}
          <div className="flex flex-col items-end gap-2">
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(invoice.totalAmount)}
            </p>
            
            {/* Amount Due (if partial) */}
            {invoice.amountDue > 0 && invoice.amountDue < invoice.totalAmount && (
              <p className="text-xs text-orange-600 font-medium">
                {formatCurrency(invoice.amountDue)} due
              </p>
            )}
            
            {/* Status Badge */}
            <span
              className="px-3 py-1 rounded-lg text-xs font-medium"
              style={(() => {
                switch(invoice.status) {
                  case 'Paid': return {background: 'linear-gradient(135deg, #052e16, #14532d)', color: '#86efac'};
                  case 'Overdue': return {background: 'linear-gradient(135deg, #450a0a, #7f1d1d)', color: '#fca5a5'};
                  case 'Partial': return {background: 'linear-gradient(135deg, #422006, #78350f)', color: '#fcd34d'};
                  case 'Unpaid':
                  case 'Pending': return {background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#93c5fd'};
                  default: return {background: 'linear-gradient(135deg, #1e293b, #334155)', color: '#94a3b8'};
                }
              })()}
            >
              {invoice.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceListItem;